import pluralize from 'pluralize'

import BaseService from './BaseService'

import { IOrder, IOrderItem, OrderTag } from '../interfaces/order'
import { IOrderGroup, IOrderGroupItem } from '../interfaces/order'
import cache from '../utils/cache'
import { http } from '../utils/http'

class OrderService extends BaseService {
  constructor(model: 'order' | 'order_group' = 'order') {
    super(model)
  }

  /**
   * Add buyer_name and location_name accessors
   * to root, for easier table sorting by column
   **/
  private refineOrders(orders): any[] {
    orders = orders.map((order) => {
      let meta_obj = {}

      try {
        meta_obj = JSON.parse(order.meta)
      } catch (error) {
        meta_obj = {}
      }

      return {
        ...order,
        meta_obj,
        buyer_name: order?.buyer?.name || '',
        location_name: order?.buyer?.location?.name || '',
        amount:
          this.model === 'order_group'
            ? order?.orders?.value?.total || 0
            : order?.amount,
      }
    })

    return orders
  }

  fetch(): Promise<IOrder[] | IOrderGroup[]> {
    return new Promise((resolve, reject) => {
      super
        .fetch()
        .then((orders: any) => {
          orders = this.refineOrders(orders)
          resolve(orders)
        })
        .catch((error) => {
          reject(error)
        })
    })
  }

  /**
   * Fetch using custom params like timestamp,
   * search and tags.
   */
  fetchCustom(params): Promise<IOrder[] | IOrderGroup[]> {
    const formData = new FormData()
    formData.append('timestamp_min', params.start_date)
    formData.append('timestamp_max', params.end_date)
    formData.append('query', '*')
    formData.append('tags[]', '*')

    return new Promise((resolve, reject) => {
      const cacheKey = `${pluralize(this.model)}_${params.start_date}_${
        params.end_date
      }`
      const cachedData = cache.get(cacheKey)
      if (cachedData) resolve(cachedData)
      else {
        try {
          http
            .post(`${this.model}/read_all_custom`, formData)
            .then((data: any) => {
              if (data.code === 200) {
                const orders = this.refineOrders(data.data)
                cache.set(cacheKey, orders)
                resolve(orders)
              } else reject({ message: data.message })
            })
        } catch (error) {
          reject({ message: 'An unexpected error occured!' })
          throw error
        }
      }
    })
  }

  fetchItems(orderId: string): Promise<IOrderItem[] | IOrderGroupItem[]> {
    return new Promise((resolve, reject) => {
      const cacheKey = `${this.model}_items_${orderId}`
      const cachedData = cache.get(cacheKey)
      if (cachedData && false) {
        resolve(cachedData)
      } else {
        try {
          http
            .get(
              `${this.model}_item/read_by_${this.model}/?${this.model}_id=${orderId}`
            )
            .then((data: any) => {
              if (data.code === 200) {
                cache.set(cacheKey, data.data)
                resolve(data.data)
              } else reject({ message: data.message })
            })
        } catch (error) {
          reject({ message: 'An unexpected error occured!' })
          throw error
        }
      }
    })
  }

  fetchBulkOrderGroupOrderItems(orderIds: string[], orderGroupId: string): Promise<IOrderItem[] | IOrderGroupItem[]> {
    const formData = new FormData()
    orderIds.forEach(_id => {
      formData.append('order_ids[]', _id)
    });
    return new Promise((resolve, reject) => {
      const cacheKey = `order_group_${orderGroupId}_${this.model}_items`
      const cachedData = cache.get(cacheKey)
      if (cachedData && false) {
        resolve(cachedData)
      } else {
        try {
          http
            .post(
              `order_item/read_all_of_orders`, 
              formData
            )
            .then((data: any) => {
              if (data.code === 200) {
                cache.set(cacheKey, data.data)
                resolve(data.data)
              } else reject({ message: data.message })
            })
        } catch (error) {
          reject({ message: 'An unexpected error occured!' })
          throw error
        }
      }
    })
  }

  fetchById = (orderId: string, fromCache?: boolean): Promise<IOrder | IOrderGroup> => {
    return new Promise((resolve, reject) => {
      const cacheKey = `${this.model}_${orderId}`
      const cachedData = cache.get(cacheKey)
      if (cachedData && fromCache) {
        resolve(cachedData)
      } else {
        try {
          http.get(`${this.model}/read/?_id=${orderId}`).then((data: any) => {
            if (data.code === 200 && data.data.length) {
              cache.set(cacheKey, data.data[0])
              resolve(data.data[0])
            } else reject({ message: data.message })
          })
        } catch (error) {
          reject({ message: 'An unexpected error occured!' })
          throw error
        }
      }
    })
  }

  fetchByTag = (
    tag: OrderTag,
    updatedAt?: Date
  ): Promise<IOrder[] | IOrderGroup[]> => {
    const formData = new FormData()
    formData.append('tag', tag)

    return new Promise((resolve, reject) => {
      const cacheKey = `${pluralize(this.model)}_${tag}_${updatedAt}`
      const cachedData = cache.get(cacheKey)
      if (cachedData) resolve(cachedData)
      else {
        try {
          http.post(`${this.model}/read_by_tag`, formData).then((data: any) => {
            if (data.code === 200) {
              cache.set(cacheKey, data.data)
              resolve(data.data)
            } else reject({ message: data.message })
          })
        } catch (error) {
          reject({ message: 'An unexpected error occured!' })
          throw error
        }
      }
    })
  }

  fetchByRef = (ref: string): Promise<IOrder | IOrderGroup> => {
    const formData = new FormData()
    formData.append('ref', ref)

    return new Promise((resolve, reject) => {
      const cacheKey = `${pluralize(this.model)}_${ref}`
      const cachedData = cache.get(cacheKey)
      if (cachedData) resolve(cachedData)
      else {
        try {
          http.post(`${this.model}/read_by_ref/`, formData).then((data: any) => {
            if (data.code === 200 && data.data.length) {
              cache.set(cacheKey, data.data[0])
              resolve(data.data[0])
            } else reject({ message: data.message })
          })
        } catch (error) {
          reject({ message: 'An unexpected error occured!' })
          throw error
        }
      }
    })
  }

  fetchByBuyer = (buyerId: string): Promise<IOrder[] | IOrderGroup[]> => {
    const formData = new FormData()
    formData.append('buyer_id', buyerId)

    return new Promise((resolve, reject) => {
      const cacheKey = `${pluralize(this.model)}_${buyerId}`
      const cachedData = cache.get(cacheKey)
      if (cachedData) {
        resolve(cachedData)
      } else {
        try {
          http
            .post(`${this.model}/read_by_buyer`, formData)
            .then((data: any) => {
              if (data.code === 200) {
                cache.set(cacheKey, data.data)
                resolve(data.data)
              } else reject({ message: data.message })
            })
        } catch (error) {
          reject({ message: 'An unexpected error occured!' })
          throw error
        }
      }
    })
  }

  fetchByBuyerTag = (
    buyerId: string,
    tag: OrderTag
  ): Promise<IOrder[] | IOrderGroup[]> => {
    const formData = new FormData()
    formData.append('buyer_id', buyerId)
    formData.append('tag', tag)

    return new Promise((resolve, reject) => {
      const cacheKey = `${pluralize(this.model)}_${buyerId}_${tag}`
      const cachedData = cache.get(cacheKey)
      if (cachedData) resolve(cachedData)
      else {
        try {
          http
            .post(`${this.model}/read_by_buyer_tag`, formData)
            .then((data: any) => {
              if (data.code === 200) {
                cache.set(cacheKey, data.data)
                resolve(data.data)
              } else reject({ message: data.message })
            })
        } catch (error) {
          reject({ message: 'An unexpected error occured!' })
          throw error
        }
      }
    })
  }

  fetchBySalesOfficer = (
    salesOfficerId: string,
    tag: OrderTag
  ): Promise<any[]> => {
    const formData = new FormData()
    formData.append('sales_officer_id', salesOfficerId)
    formData.append('tag', tag)

    return new Promise((resolve, reject) => {
      const cacheKey = `orders_${salesOfficerId}`
      const cachedData = cache.get(cacheKey)
      if (cachedData) {
        resolve(cachedData)
      } else {
        try {
          http
            .post('order/read_by_sales_officer_tag_group_by_buyer', formData)
            .then((data: any) => {
              if (data.code === 200) {
                /** Orders grouped by buyers */
                let order_groups: any[] = []

                if (typeof data.data === 'object') {
                  order_groups = Object.values(data.data)
                }

                cache.set(cacheKey, order_groups)
                resolve(order_groups)
              } else reject({ message: data.message })
            })
        } catch (error) {
          reject({ message: 'An unexpected error occured!' })
          throw error
        }
      }
    })
  }

  historyBySalesOfficer = (salesOfficerId: string): Promise<any[]> => {
    const formData = new FormData()
    formData.append('sales_officer_id', salesOfficerId)

    return new Promise((resolve, reject) => {
      const cacheKey = `order_history_${salesOfficerId}`
      const cachedData = cache.get(cacheKey)
      if (cachedData) {
        resolve(cachedData)
      } else {
        try {
          http
            .post('order/history_by_sales_officer', formData)
            .then((data: any) => {
              if (data.code === 200) {
                cache.set(cacheKey, data.data)
                resolve(data.data)
              } else reject({ message: data.message })
            })
        } catch (error) {
          reject({ message: 'An unexpected error occured!' })
          throw error
        }
      }
    })
  }

  createItem(orderItem: IOrderItem | IOrderGroupItem): Promise<any> {
    return new Promise((resolve, reject) => {
      const formData = new FormData()
      // formData.append('amount', item.amount.toString())
      // formData.append('quantity', item.quantity.toString())
      // formData.append('product_id', item.product_id)
      // formData.append('order_id', item.order_id)

      Object.keys(orderItem).forEach((key) => {
        formData.append(key, orderItem[key])
      })

      try {
        http.post(`${this.model}_item/create`, formData).then((data: any) => {
          if (data.code === 200) {
            cache.reset()
            resolve(data.data) // data.data is item._id
          } else reject({ message: data.message })
        })
      } catch (error) {
        reject({ message: 'An unexpected error occured!' })
        throw error
      }
    })
  }

  updateAmount(details: { _id: string; amount: string }): Promise<void> {
    return new Promise((resolve, reject) => {
      const formData = new FormData()
      // formData.append('_id', updatedOrder._id)
      // formData.append('amount', parseFloat(updatedOrder.amount).toFixed(2))

      Object.keys(details).forEach((key) => {
        formData.append(key, details[key])
      })

      try {
        http.post(`${this.model}/update_amount`, formData).then((data: any) => {
          if (data.code === 200) {
            cache.reset()
            resolve()
          } else reject({ message: data.message })
        })
      } catch (error) {
        reject({ message: 'An unexpected error occured!' })
        throw error
      }
    })
  }

  updateTag(details: {
    _id: string
    old_tag: OrderTag
    new_tag: OrderTag
    sales_officer_id?: string
    meta: string
  }): Promise<void> {
    return new Promise((resolve, reject) => {
      const formData = new FormData()

      Object.keys(details).forEach((key) => {
        formData.append(key, details[key])
      })

      try {
        http.post(`${this.model}/update_tag`, formData).then((data: any) => {
          if (data.code === 200) {
            cache.reset()
            resolve()
          } else reject({ message: data.message })
        })
      } catch (error) {
        reject({ message: 'An unexpected error occured!' })
        throw error
      }
    })
  }

  /**
   * Loop over an array of orders and update
   * their tag
   *
   * @param {array} orders
   * @param {string} newTag
   */
  updateTagBulk(orders: IOrder[], newTag: OrderTag): Promise<void> {
    return new Promise((resolve, reject) => {
      orders.forEach((order) => {
        Promise.all([
          this.updateTag({
            _id: order._id,
            old_tag: order.tag,
            new_tag: newTag,
            sales_officer_id: order.sales_officer_id,
            meta: order.meta,
          }),
        ])
          .then(() => resolve())
          .catch(() => reject({ message: 'An unexpected error occured!' }))
      })
    })
  }

  updateMeta(details: { _id: string; meta: string }): Promise<void> {
    return new Promise((resolve, reject) => {
      const formData = new FormData()

      Object.keys(details).forEach((key) => {
        formData.append(key, details[key])
      })

      try {
        http.post(`${this.model}/update_meta`, formData).then((data: any) => {
          if (data.code === 200) {
            cache.reset()
            resolve()
          } else reject({ message: data.message })
        })
      } catch (error) {
        reject({ message: 'An unexpected error occured!' })
        throw error
      }
    })
  }

  updateItem(orderItem: IOrderItem | IOrderGroupItem): Promise<void> {
    return new Promise((resolve, reject) => {
      const formData = new FormData()

      Object.keys(orderItem).forEach((key) => {
        formData.append(key, orderItem[key])
      })

      try {
        http.post(`${this.model}_item/update`, formData).then((data: any) => {
          if (data.code === 200) {
            cache.reset()
            resolve()
          } else reject({ message: data.message })
        })
      } catch (error) {
        reject({ message: 'An unexpected error occured!' })
        throw error
      }
    })
  }

  deleteItem(details: { _id: string }): Promise<void> {
    return new Promise((resolve, reject) => {
      const formData = new FormData()
      formData.append('_id', details._id)

      try {
        http.post(`${this.model}_item/delete`, formData).then((data: any) => {
          if (data.code === 200) {
            cache.reset()
            resolve()
          } else reject({ message: data.message })
        })
      } catch (error) {
        reject({ message: 'An unexpected error occured!' })
        throw error
      }
    })
  }

  upsertItem(orderItem: IOrderItem | IOrderGroupItem): Promise<any> {
    const formData = new FormData()

    Object.keys(orderItem).forEach((key) => {
      formData.append(key, orderItem[key])
    })

    return new Promise((resolve, reject) => {
      try {
        http.post(`${this.model}_item/upsert`, formData).then((data: any) => {
          if (data.code === 200) {
            // cache.reset()
            resolve(data.data)
          } else reject({ message: data.message })
        })
      } catch (error) {
        reject({ message: 'An unexpected error occured!' })
        throw error
      }
    })
  }

  removeItem(details: { _id: string }): Promise<any> {
    const formData = new FormData()
    formData.append('_id', details._id)

    return new Promise((resolve, reject) => {
      try {
        http.post(`${this.model}_item/remove`, formData).then((data: any) => {
          if (data.code === 200) {
            cache.reset()
            resolve(data.data)
          } else reject({ message: data.message })
        })
      } catch (error) {
        reject({ message: 'An unexpected error occured!' })
        throw error
      }
    })
  }

  private refineMatrix(data): any {
    const matrix_fulfilments = data
    const fulfilments_array = []

    Object.entries(matrix_fulfilments).forEach((item) => {
      const key = item[0]
      const fulfilment: any = item[1]

      fulfilment.key = key

      const manufacturers_array = []
      Object.entries(fulfilment.manufacturers).forEach((item) => {
        const key = item[0]
        const manufacturer: any = item[1]

        manufacturer.key = key
        manufacturers_array.push(manufacturer)
      })

      fulfilment.manufacturers = manufacturers_array
      fulfilments_array.push(fulfilment)
    })

    return {
      columns: fulfilments_array[0].manufacturers,
      data: fulfilments_array,
    }
  }

  /**
   * Fetch order fulfilment [manufacturer ==> seller]
   * by buyer location
   */
  fetchRouteFulfilment(locationId: string): Promise<any> {
    const formData = new FormData()
    formData.append('location_id', locationId)

    return new Promise((resolve, reject) => {
      const cacheKey = `fulfilment_${locationId}`
      const cachedData = cache.get(cacheKey)
      if (cachedData) resolve(cachedData)
      else {
        try {
          http.post('order/route_fulfilment_all', formData).then((data: any) => {
            if (data.code === 200) {
              const fulfilment_array: string[] = Object.values(data.data)
              const fulfilment_obj = {}

              if (fulfilment_array.length) {
                fulfilment_array
                  .filter((item: any) => item.found)
                  .forEach((item: any) => {
                    // return an array of just maunfacturer_id
                    // return fulfilment.manufacturer_id
                    fulfilment_obj[item.manufacturer_id] = item
                  })
                cache.set(cacheKey, fulfilment_obj)
                resolve(fulfilment_obj)
              }

              reject({ message: 'Route fulfilment unavailable' })
            } else reject({ message: 'Route fulfilment unavailable' })
          })
        } catch (error) {
          reject({ message: 'An unexpected error occured!' })
          throw error
        }
      }
    })
  }

  fetchMatrixFulfilment(buyers?: boolean): Promise<any> {
    const endpoint = !buyers ? 'matrix_fulfilment' : 'matrix_fulfilment_buyers'
    return new Promise((resolve, reject) => {
      const cacheKey = endpoint
      const cachedData = cache.get(cacheKey)
      if (cachedData) resolve(cachedData)
      else {
        try {
          this.http.get(`order/${endpoint}`).then((data: any) => {
            if (data.code === 200) {
              const matrix_data = Object.keys(data.data)

              if (matrix_data.length) {
                const refinedData = this.refineMatrix(data.data)
                cache.set(cacheKey, refinedData)
                resolve(refinedData)
              } else reject()
            } else reject(data)
          })
        } catch (error) {
          reject(error)
          throw error
        }
      }
    })
  }
}

export default OrderService
